import { useState, useEffect, useCallback } from 'react';

const cache = {
  viewHistory: { data: [], fetchedAt: 0, userId: null },
};

let inFlightViewHistory = null;

// TTL: 10 minutes (view history changes very slowly)
const VIEW_HISTORY_TTL_MS = 600000;

// Listeners: any mounted hook instance registers a forceUpdate here
const listeners = new Set();
function notifyListeners() {
  listeners.forEach((fn) => fn());
}

async function fetchViewHistoryData(userId, force = false) {
  const now = Date.now();

  // If userId changed, invalidate cache
  if (cache.viewHistory.userId !== userId) {
    cache.viewHistory = { data: [], fetchedAt: 0, userId: null };
  }

  if (!force && cache.viewHistory.fetchedAt && now - cache.viewHistory.fetchedAt < VIEW_HISTORY_TTL_MS) {
    return cache.viewHistory.data;
  }

  // Dedupe: if a request is already in-flight, wait for it
  if (inFlightViewHistory) {
    return inFlightViewHistory;
  }

  const promise = (async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/influencer/${userId}/views/history`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();

        // Generate last 7 days ending with today
        const days = [];
        const today = new Date();
        for (let i = 6; i >= 0; i -= 1) {
          const d = new Date(today);
          d.setDate(today.getDate() - i);
          days.push(d.toISOString().slice(0, 10));
        }

        const dataMap = {};
        data.forEach((item) => {
          dataMap[item.date.slice(0, 10)] = item.views;
        });

        // Fill in missing days with 0 views
        const filled = days.map((date) => ({
          date,
          views: dataMap[date] || 0,
        }));

        cache.viewHistory = { data: filled, fetchedAt: Date.now(), userId };
        return filled;
      }
      throw new Error('Failed to fetch view history');
    } finally {
      inFlightViewHistory = null;
    }
  })();

  inFlightViewHistory = promise;
  return promise;
}

/**
 * Shared hook for influencer view history data.
 * Uses module-level cache so data is shared across all mounted components.
 *
 * @param {string} userId - The influencer user ID
 * @returns {{ viewHistory: Array, loading: boolean, error: string|null, refresh: Function, lastUpdated: number }}
 */
/** Resets module cache (for tests and rare logout flows). */
export function resetViewHistoryCache() {
  cache.viewHistory = { data: [], fetchedAt: 0, userId: null };
  inFlightViewHistory = null;
}

export default function useViewHistory(userId) {
  const [viewHistory, setViewHistory] = useState(
    cache.viewHistory.userId === userId ? cache.viewHistory.data : [],
  );
  const [loading, setLoading] = useState(
    !cache.viewHistory.fetchedAt || cache.viewHistory.userId !== userId,
  );
  const [error, setError] = useState(null);

  // Sync local state from module cache
  const syncFromCache = useCallback(() => {
    if (cache.viewHistory.userId === userId) {
      setViewHistory(cache.viewHistory.data);
    }
  }, [userId]);

  // Register this instance as a listener
  useEffect(() => {
    listeners.add(syncFromCache);
    return () => {
      listeners.delete(syncFromCache);
    };
  }, [syncFromCache]);

  // Refresh function callable by consumers
  const refresh = useCallback(async (force = false) => {
    if (!userId) return;
    try {
      setError(null);
      const data = await fetchViewHistoryData(userId, force);
      setViewHistory(data);
      notifyListeners();
    } catch (err) {
      setError(err.message || 'Failed to load view history.');
    }
  }, [userId]);

  // Initial fetch on mount or userId change
  useEffect(() => {
    if (!userId) return;

    let cancelled = false;

    const loadData = async () => {
      if (cache.viewHistory.fetchedAt && cache.viewHistory.userId === userId) {
        setViewHistory(cache.viewHistory.data);
        setLoading(false);

        // Refresh in background if stale
        const now = Date.now();
        if (now - cache.viewHistory.fetchedAt >= VIEW_HISTORY_TTL_MS) {
          try {
            await refresh(false);
          } catch {
            // Ignore background refresh errors
          }
        }
        return;
      }

      setLoading(true);
      try {
        const data = await fetchViewHistoryData(userId, false);
        if (!cancelled) {
          setViewHistory(data);
          notifyListeners();
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to load view history.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => { // eslint-disable-line consistent-return
      cancelled = true;
    };
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    viewHistory,
    loading,
    error,
    refresh,
    lastUpdated: cache.viewHistory.fetchedAt,
  };
}
